import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Mail, Send, Inbox, ArrowUpRight, Trash2, Loader2, RefreshCw, ArrowLeft, Circle, CheckCircle2, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = '/api';

function adminApi() {
  const token = localStorage.getItem('admin_token');
  return axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${token}` },
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export default function AdminEmailPanel() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: '', subject: '', body: '', from_email: 'info@laizyleads.de' });
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadEmails = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { direction: filter } : {};
      const res = await adminApi().get('/admin/emails', { params });
      setEmails(res.data.emails || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { toast.error('E-Mails konnten nicht geladen werden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const openEmail = async (email) => {
    try {
      const res = await adminApi().get(`/admin/emails/${email.id}`);
      setSelectedEmail(res.data);
      if (!email.is_read && email.direction === 'inbound') {
        setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch { toast.error('E-Mail konnte nicht geöffnet werden'); }
  };

  const deleteEmail = async (id) => {
    try {
      await adminApi().delete(`/admin/emails/${id}`);
      setEmails(prev => prev.filter(e => e.id !== id));
      if (selectedEmail?.id === id) setSelectedEmail(null);
      toast.success('E-Mail gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const sendEmail = async () => {
    if (!compose.to || !compose.subject || !compose.body) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    setSending(true);
    try {
      await adminApi().post('/admin/emails/send', compose);
      toast.success('E-Mail gesendet!');
      setComposeOpen(false);
      setCompose({ to: '', subject: '', body: '', from_email: 'info@laizyleads.de' });
      loadEmails();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    }
    setSending(false);
  };

  const replyToEmail = () => {
    if (!selectedEmail) return;
    setCompose({
      to: selectedEmail.from_email,
      subject: `Re: ${selectedEmail.subject}`,
      body: '',
      from_email: selectedEmail.to_email || 'info@laizyleads.de',
    });
    setComposeOpen(true);
  };

  const filtered = emails.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.from_email || '').toLowerCase().includes(s) ||
      (e.to_email || '').toLowerCase().includes(s) ||
      (e.subject || '').toLowerCase().includes(s);
  });

  if (selectedEmail) {
    return (
      <div className="space-y-4" data-testid="email-detail-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} data-testid="email-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div className="flex-1" />
          {selectedEmail.direction === 'inbound' && (
            <Button size="sm" onClick={replyToEmail} data-testid="email-reply-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Antworten
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteEmail(selectedEmail.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-white">{selectedEmail.subject}</h2>
              <div className="flex items-center gap-3 mt-2 text-sm text-slate-400">
                <Badge variant="outline" className={selectedEmail.direction === 'inbound' ? 'border-blue-500/30 text-blue-400' : 'border-emerald-500/30 text-emerald-400'}>
                  {selectedEmail.direction === 'inbound' ? 'Empfangen' : 'Gesendet'}
                </Badge>
                <span>{formatDate(selectedEmail.created_at)}</span>
              </div>
            </div>
            <div className="text-sm space-y-1 text-slate-400 border-y border-slate-800 py-3">
              <p><span className="text-slate-500">Von:</span> {selectedEmail.from_email}</p>
              <p><span className="text-slate-500">An:</span> {selectedEmail.to_email}</p>
            </div>
            <div className="prose prose-invert prose-sm max-w-none">
              {selectedEmail.html_body ? (
                <div dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }} />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans">{selectedEmail.text_body}</pre>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="email-inbox-view">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
          {[
            { key: 'all', label: 'Alle' },
            { key: 'inbound', label: 'Empfangen' },
            { key: 'outbound', label: 'Gesendet' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filter === f.key ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid={`email-filter-${f.key}`}>
              {f.label} {f.key === 'inbound' && unreadCount > 0 && <span className="ml-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 h-8 bg-slate-900 border-slate-800 text-sm" data-testid="email-search" />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="ghost" size="sm" onClick={() => { setLoading(true); loadEmails(); }} data-testid="email-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setComposeOpen(true)} data-testid="email-compose-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Neue E-Mail
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Mail className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Keine E-Mails vorhanden</p>
          <p className="text-slate-600 text-xs mt-1">Eingehende E-Mails erscheinen hier sobald der Resend Inbound-Webhook konfiguriert ist</p>
        </div>
      ) : (
        <div className="space-y-1">
          {filtered.map(email => (
            <div key={email.id} onClick={() => openEmail(email)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors border ${
                !email.is_read && email.direction === 'inbound'
                  ? 'bg-slate-900/80 border-blue-500/20 hover:bg-slate-800/80'
                  : 'bg-slate-900/40 border-slate-800/50 hover:bg-slate-800/60'
              }`}
              data-testid={`email-row-${email.id}`}>
              <div className="shrink-0">
                {email.direction === 'inbound' ? (
                  !email.is_read ? <Circle className="w-2.5 h-2.5 fill-blue-400 text-blue-400" /> : <CheckCircle2 className="w-3.5 h-3.5 text-slate-600" />
                ) : (
                  <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate ${!email.is_read && email.direction === 'inbound' ? 'font-semibold text-white' : 'text-slate-300'}`}>
                    {email.direction === 'inbound' ? email.from_email : email.to_email}
                  </span>
                </div>
                <p className={`text-sm truncate ${!email.is_read && email.direction === 'inbound' ? 'text-slate-200' : 'text-slate-400'}`}>
                  {email.subject}
                </p>
                <p className="text-xs text-slate-600 truncate mt-0.5">
                  {stripHtml(email.html_body || email.text_body || '').substring(0, 80)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <span className="text-[11px] text-slate-600">{formatDate(email.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>E-Mail verfassen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-10 shrink-0">Von:</span>
              <select value={compose.from_email} onChange={e => setCompose(p => ({ ...p, from_email: e.target.value }))}
                className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="compose-from">
                <option value="info@laizyleads.de">info@laizyleads.de</option>
                <option value="support@laizyleads.de">support@laizyleads.de</option>
                <option value="noreply@laizyleads.de">noreply@laizyleads.de</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-10 shrink-0">An:</span>
              <Input value={compose.to} onChange={e => setCompose(p => ({ ...p, to: e.target.value }))}
                placeholder="empfaenger@beispiel.de" className="flex-1" data-testid="compose-to" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-10 shrink-0">Betr.:</span>
              <Input value={compose.subject} onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))}
                placeholder="Betreff" className="flex-1" data-testid="compose-subject" />
            </div>
            <Textarea value={compose.body} onChange={e => setCompose(p => ({ ...p, body: e.target.value }))}
              placeholder="Nachricht schreiben..." rows={8} className="resize-none" data-testid="compose-body" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setComposeOpen(false)}>Abbrechen</Button>
            <Button onClick={sendEmail} disabled={sending} data-testid="compose-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
